import { useCallback } from "react"
import { useNavigate } from "react-router"
import api from "../../api"
import { getCurrentUser, getToken, hasCurrentPolicy, needsOnboarding, persistAuthSession } from "../../auth"
import { askConfirm } from "../../askConfirm"
import { runTour, waitForElement } from "../runTour"
import { TUTORIAL_PARTS, type TutorialPart } from "./index"

async function completeOnboarding(): Promise<void> {
  const token = getToken()
  const user = getCurrentUser()
  if (!token || !user) return
  try {
    const res = await api.patch("/auth/me/onboarding")
    const onboardedAt = res.data?.data?.onboardedAt ?? new Date().toISOString()
    persistAuthSession(token, { ...user, onboardedAt })
  } catch {
    // Best-effort: if this fails, the tutorial just shows again next login —
    // never worth surfacing an error over.
  }
}

/**
 * Runs one part's steps and resolves once it closes — either by finishing
 * the last step, or by being dismissed (X, Escape, overlay click). Both
 * count the same way here: the part is over, move on. Per spec, closing
 * the tutorial early still marks it as seen — it never blocks the panel —
 * so there is no case where the sequence needs to hard-stop instead of
 * just letting the loop below finish and mark onboarding done.
 */
function runPart(part: TutorialPart): Promise<void> {
  return new Promise((resolve) => {
    runTour(part.steps, { onDestroyed: () => resolve() })
  })
}

export function useOnboardingTutorial() {
  const navigate = useNavigate()

  const run = useCallback(async () => {
    const applicableParts = TUTORIAL_PARTS.filter((part) => hasCurrentPolicy(...part.requiredPolicies))

    for (let i = 0; i < applicableParts.length; i++) {
      const part = applicableParts[i]
      navigate(part.route)
      const ready = await waitForElement(part.waitForSelector)
      if (!ready) continue

      // The last step of a part is often the real click that navigates
      // the admin somewhere useful (e.g. into the create-student form) —
      // that page is the whole point, so the sequence must NOT immediately
      // steal focus away to the next part. It pauses here and asks before
      // moving on, instead of racing the just-completed navigation.
      await runPart(part)
      const next = applicableParts[i + 1]
      if (next) {
        const continueToNext = await askConfirm({
          title: `Parte ${i + 1} de ${applicableParts.length} lista`,
          message: `¿Seguimos con la parte ${i + 2}: "${next.title}"?`,
          confirmLabel: "Seguir",
        })
        if (!continueToNext) break
      }
    }

    await completeOnboarding()
  }, [navigate])

  const runIfNeeded = useCallback(() => {
    if (needsOnboarding()) {
      void run()
    }
  }, [run])

  return { run, runIfNeeded }
}
